import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { makeStyles, Paper } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import Softphone from "../Softphone";
import FabioHelpBanner from "../FabioHelpBanner";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const TICKET_LIST_SYNC_EVENT = "ticket-list-sync";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen, setCurrentTicket } = useContext(TicketsContext);


  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const { companyId } = user;

  const shouldLeaveCurrentView = useCallback((updatedTicket) => {
    if (!updatedTicket) return false;

    if (updatedTicket.status === "closed") {
      return true;
    }

    if (updatedTicket.userId && updatedTicket.userId !== user?.id) {
      return true;
    }

    return false;
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {

          if (!isNil(ticketId) && ticketId !== "undefined") {

            const { data } = await api.get("/tickets/u/" + ticketId);

            setContact(data.contact);
            // setWhatsapp(data.whatsapp);
            // setQueueId(data.queueId);
            setTicket(data);
            if (["pending", "open", "group"].includes(data.status)) {
              setTabOpen(data.status);
            }
            setLoading(false);
          }
        } catch (err) {
          history.push("/tickets");   // correção para evitar tela branca uuid não encontrado Feito por Altemir 16/08/2023
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history]);

  useEffect(() => {
    const refreshCurrentTicket = async (event) => {
      const syncedTicketId = event?.detail?.ticketId;

      if (!syncedTicketId || (syncedTicketId !== ticket.id && String(syncedTicketId) !== String(ticket.id))) {
        return;
      }

      try {
        const { data } = await api.get("/tickets/u/" + ticketId);
        setContact(data.contact);
        setTicket(data);
        setCurrentTicket({ ...data, code: "#" + data.status });
        if (["pending", "open", "group"].includes(data.status)) {
          setTabOpen(data.status);
        }
      } catch (err) {
        // The normal socket update remains the fallback if this short refresh fails.
        console.warn("No fue posible sincronizar el ticket actualizado", err);
      }
    };

    window.addEventListener(TICKET_LIST_SYNC_EVENT, refreshCurrentTicket);
    return () => window.removeEventListener(TICKET_LIST_SYNC_EVENT, refreshCurrentTicket);
  }, [ticketId, ticket.id, setCurrentTicket, setTabOpen]);

  useEffect(() => {
    if (!ticket && !ticket.id && ticket.uuid !== ticketId && ticketId === "undefined") {
      return;
    }

    if (user.companyId) {
      // ✅ CORREÇÃO: Usar ticket.uuid em vez de ticket.id para consistência
      const ticketIdentifier = ticket.uuid || ticketId;

      console.log("[Ticket socket]", {
        routeTicketId: ticketId,
        loadedTicketId: ticket?.id || null,
        loadedTicketUuid: ticket?.uuid || null,
        ticketIdentifier
      });

      const onConnectTicket = () => {
        socket.emit("joinChatBox", ticketIdentifier);
      }

      const onCompanyTicket = (data) => {
        const sameTicket =
          data?.ticket?.id === ticket?.id ||
          data?.ticket?.uuid === (ticket?.uuid || ticketId);

        if (data.action === "update" && sameTicket) {
          if (shouldLeaveCurrentView(data.ticket)) {
            setCurrentTicket({ id: null, code: null });
            history.replace("/tickets");
            return;
          }

          setTicket(data.ticket);
        }

        if (data.action === "delete" && data.ticketId === ticket?.id) {
          setCurrentTicket({ id: null, code: null });
          history.replace("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          // if (isMounted) {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
          // }
        }
      };

      socket.on("connect", onConnectTicket)
      socket.on(`company-${companyId}-ticket`, onCompanyTicket);
      socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

      return () => {
        socket.emit("joinChatBoxLeave", ticketIdentifier);
        socket.off("connect", onConnectTicket);
        socket.off(`company-${companyId}-ticket`, onCompanyTicket);
        socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
      };
    }
  }, [ticketId, ticket, history, companyId, setCurrentTicket, shouldLeaveCurrentView, socket]);

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          isGroup={ticket.isGroup}
          onDrop={setDragDropFiles}
          whatsappId={ticket.whatsappId}
          queueId={ticket.queueId}
          channel={ticket.channel}
          contactId={ticket.contact?.id}
          ticket={ticket}
        >
        </MessagesList>
        <MessageInput
          ticketId={ticket.uuid || ticketId}
          ticketStatus={ticket.status}
          ticketChannel={ticket.channel}
          droppedFiles={dragDropFiles}
          contactId={contact.id}
          ticketUserId={ticket.userId}
          currentUserId={user?.id}
        />
      </>
    );
  };


  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        {/* <div id="TicketHeader"> */}
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div id="TicketHeader">
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={handleDrawerOpen}
              />
            </div>
          )}
          <TicketActionButtons
            ticket={ticket}
            onTicketUpdated={setTicket}
          />
          <Softphone contact={contact} ticket={ticket} />
        </TicketHeader>
        {/* </div> */}
        <Paper>
          <TagsContainer contact={contact} />
        </Paper>
        {ticket.id && <FabioHelpBanner ticketId={ticket.id} />}
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              {renderMessagesList()}
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />

    </div>
  );
};

export default Ticket;
